import { PermissionGuard } from '@/components/PermissionGuard';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useAddLineToCheckout } from '@/hooks/useCheckoutActions';
import { useOperators } from '@/hooks/useOperator';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import type { AddLineToCheckoutBody as AddLineToCheckoutRequest } from '@octave/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as z from 'zod';
import { CarteSimStep } from './CarteSimStep';
import { CollaborateurStep } from './CollaborateurStep';
import { ForfaitStep } from './ForfaitStep';
import { OperationStep } from './OperationStep';
import { StepProgressBar } from './StepProgressBar';

const commandLineSchema = z
  .object({
    siteId: z.string({ required_error: 'Centre de facturation requis' }),
    identityId: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    simId: z.string().optional(),
    simType: z.enum(['physique', 'esim'], {
      required_error: 'Type de SIM requis',
    }),
    physiqueOption: z.enum(['order', 'stock']).optional(),
    operatorLabel: z.string(),
    operatorId: z.string(),
    planId: z.string(),
    orderNature: z.enum(['creation', 'portability'], {
      required_error: 'Nature de la commande requise',
    }),
    mobileLineType: z.enum(['classic', 'dataonly']),
    portedNumber: z.string().optional(),
    rioNumber: z.string().optional(),
    portabilityDate: z.date().optional(),
  })
  .refine(
    (data) => !!data.identityId || (!!data.firstName && !!data.lastName),
    {
      message:
        'Soit un identifiant utilisateur, soit prénom et nom sont requis',
      path: [],
    }
  )
  .superRefine((data, ctx) => {
    if (data.simType === 'physique' && !data.physiqueOption) {
      ctx.addIssue({
        path: ['physiqueOption'],
        code: z.ZodIssueCode.custom,
        message: "L'option de SIM physique est requise.",
      });
    }
    if (data.orderNature === 'portability') {
      if (!data.portedNumber) {
        ctx.addIssue({
          path: ['portedNumber'],
          code: z.ZodIssueCode.custom,
          message: 'Le numéro de ligne à conserver est requis.',
        });
      }
      if (!data.rioNumber) {
        ctx.addIssue({
          path: ['rioNumber'],
          code: z.ZodIssueCode.custom,
          message: 'Le numéro RIO est requis.',
        });
      }
      if (!data.portabilityDate) {
        ctx.addIssue({
          path: ['portabilityDate'],
          code: z.ZodIssueCode.custom,
          message: 'La date de portabilité est requise.',
        });
      }
    }
  })
  .refine(
    (data) => {
      if (data.simType === 'physique' && data.physiqueOption === 'stock') {
        return !!data.simId;
      }
      return true;
    },
    {
      message: 'Veuillez sélectionner une carte SIM',
      path: ['simId'],
    }
  );

export type CommandLineForm = z.infer<typeof commandLineSchema>;

type Step = 'collaborateur' | 'forfait' | 'operation' | 'carte-sim';

const STEPS = [
  { id: 'collaborateur' as const, label: 'Collaborateur' },
  { id: 'forfait' as const, label: 'Forfait' },
  { id: 'operation' as const, label: 'Opération' },
  { id: 'carte-sim' as const, label: 'Carte SIM' },
];

export function CommandLineView() {
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: organization } = useCurrentOrganization();
  const { data: operators } = useOperators();
  const { mutate: addLineToCheckout, isPending } = useAddLineToCheckout();
  const [searchParams] = useSearchParams();
  const prefilledOperatorId = searchParams.get('operatorId') || '';
  const prefilledPlanId = searchParams.get('planId') || '';
  const prefilledLineType =
    (searchParams.get('mobileLineType') as 'classic' | 'dataonly' | null) ||
    'classic';
  const [currentStep, setCurrentStep] = useState<Step>('collaborateur');
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(0);

  const form = useForm<CommandLineForm>({
    resolver: zodResolver(commandLineSchema),
    defaultValues: {
      siteId: '',
      identityId: '',
      firstName: '',
      lastName: '',
      simId: '',
      simType: 'physique',
      physiqueOption: 'order',
      planId: prefilledPlanId,
      orderNature: 'creation',
      operatorId: prefilledOperatorId,
      operatorLabel: '',
      mobileLineType: prefilledLineType,
      portedNumber: '',
      rioNumber: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!prefilledOperatorId || !operators) return;
    const op = operators.find((o) => o.id === prefilledOperatorId);
    if (op && !form.getValues('operatorLabel')) {
      form.setValue('operatorLabel', op.name);
    }
  }, [operators, prefilledOperatorId, form]);

  // Watch all relevant fields to trigger re-renders when they change
  form.watch(['siteId', 'identityId', 'firstName', 'lastName', 'operatorId', 'planId', 'orderNature', 'portedNumber', 'rioNumber', 'portabilityDate']);

  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  const isStepValid = (stepId: Step): boolean => {
    const values = form.getValues();
    switch (stepId) {
      case 'collaborateur':
        return !!values.siteId && !!(values.identityId || (values.firstName && values.lastName));
      case 'forfait':
        return !!values.operatorId && !!values.planId;
      case 'operation':
        if (values.orderNature === 'portability') {
          return !!values.portedNumber && !!values.rioNumber && !!values.portabilityDate;
        }
        return !!values.orderNature;
      case 'carte-sim':
        return true;
      default:
        return false;
    }
  };

  const canGoNext = isStepValid(currentStep);

  const goToNextStep = () => {
    if (currentIndex < STEPS.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentStep(STEPS[nextIndex].id);
      setMaxVisitedIndex((prev) => Math.max(prev, nextIndex));
    }
  };

  const goToPreviousStep = () => {
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].id);
    }
  };

  const handleAddLineToCheckout = () => {
    if (!organization) return;
    const data = form.getValues();

    const input: AddLineToCheckoutRequest = {
      organizationId: organization.id,
      siteId: data.siteId,
      identityId: data.identityId || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      operatorId: data.operatorId,
      planId: data.planId,
      simId: data.simId || '',
      simType: data.simType as 'physique' | 'esim',
      physiqueOption: (data.physiqueOption || 'order') as 'order' | 'stock',
      orderNature: data.orderNature,
      mobileLineType: data.mobileLineType,
      portedNumber: data.portedNumber || undefined,
      rioNumber: data.rioNumber || undefined,
      portabilityDate: data.portabilityDate
        ? data.portabilityDate.toISOString()
        : undefined,
    };

    addLineToCheckout(input);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'collaborateur':
        return <CollaborateurStep form={form} />;
      case 'forfait':
        return <ForfaitStep form={form} />;
      case 'operation':
        return <OperationStep form={form} />;
      case 'carte-sim':
        return <CarteSimStep form={form} />;
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/o/${tenantSlug}/lines`)}
          className="text-slate-500 hover:text-slate-700 -ml-2 mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Button>
        <h1 className="text-2xl font-bold text-slate-900">
          Commander une ligne
        </h1>
        <p className="text-sm text-slate-500">
          Configurez votre nouvelle ligne mobile en quelques étapes.
        </p>
      </div>

      {/* Card container */}
      <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-sm space-y-8">
        {/* Progress bar */}
        <StepProgressBar
          steps={STEPS}
          currentIndex={currentIndex}
          maxVisitedIndex={maxVisitedIndex}
          canNavigateTo={(i) => {
            if (i <= currentIndex) return true;
            for (let s = currentIndex; s < i; s++) {
              if (!isStepValid(STEPS[s].id)) return false;
            }
            return true;
          }}
          onStepClick={(i) => setCurrentStep(STEPS[i].id)}
        />

        {/* Step content */}
        <Form {...form}>
          <form className="space-y-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-slate-200">
              <Button
                type="button"
                variant="ghost"
                onClick={goToPreviousStep}
                disabled={currentIndex === 0}
                className="text-slate-600"
              >
                Précédent
              </Button>

              {currentStep === 'carte-sim' ? (
                <PermissionGuard permission="write">
                  <Button
                    type="button"
                    onClick={handleAddLineToCheckout}
                    disabled={
                      form.formState.isSubmitting ||
                      (form.watch('physiqueOption') === 'stock' &&
                        !form.watch('simId')) ||
                      isPending
                    }
                    className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
                  >
                    <ShoppingCart className="h-3.5 w-3.5" />
                    Ajouter au panier
                  </Button>
                </PermissionGuard>
              ) : (
                <Button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!canGoNext || isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all"
                >
                  Continuer
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
